import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, X } from 'lucide-react';
import { productsData } from '../data/ecofinexData';

const ShadesGallery = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);

  // Get unique finish types for filters
  const finishTypes = ['All', ...new Set(productsData.map(p => p.finishType))];

  // Handle search - productsData is a static import, setters are stable from useState
  useEffect(() => {
    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase();
      const results = productsData.filter(product => 
        product.shadeName.toLowerCase().includes(query) ||
        product.productCode.toLowerCase().includes(query) ||
        product.finishType.toLowerCase().includes(query)
      ).slice(0, 8);
      setSearchResults(results);
      setShowSuggestions(true);
    } else {
      setSearchResults([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  // Close suggestions when clicking outside - runs once on mount
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter products based on active filter and search
  const filteredProducts = productsData.filter(product => {
    const matchesFilter = activeFilter === 'All' || product.finishType === activeFilter;
    const matchesSearch = searchQuery.trim() === '' || 
      product.shadeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.productCode.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const handleProductClick = (productId) => {
    setShowSuggestions(false);
    navigate(`/product/${productId}`);
  };

  const handleSuggestionClick = (productId) => {
    setShowSuggestions(false);
    setSearchQuery('');
    navigate(`/product/${productId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => navigate('/')}
              data-testid="back-to-home-btn"
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-blue-600 transition-colors font-medium"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
            <a href="/" className="flex items-center">
              <img 
                src="/ecofinex-logo.png" 
                alt="EcoFinex Logo" 
                className="h-10"
              />
            </a>
            <div className="w-24"></div>
          </div>
        </div>
      </nav>

      {/* Shades Gallery Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Shade Collection</h1>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore our comprehensive collection of 200+ powder coating resins across 8 finish categories. 
              Each shade is engineered for superior performance and stunning aesthetics.
            </p>
          </div>

          {/* Search Bar */}
          <div ref={searchRef} className="relative max-w-2xl mx-auto mb-8">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.length > 0 && setShowSuggestions(true)}
                placeholder="Search shades by name or code (e.g., 'Siemens Grey' or 'EF-0322')"
                data-testid="shade-search-input"
                className="w-full pl-12 pr-12 py-4 rounded-2xl border-2 border-gray-200 focus:border-blue-500 focus:outline-none transition-all text-gray-900 bg-white shadow-sm hover:shadow-md focus:shadow-lg"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setShowSuggestions(false);
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Search Suggestions */}
            {showSuggestions && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden z-50">
                <div className="p-2">
                  <p className="px-3 py-2 text-xs text-gray-500 uppercase tracking-wide">Suggestions</p>
                  {searchResults.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleSuggestionClick(product.id)}
                      data-testid={`search-result-${product.id}`}
                      className="w-full flex items-center gap-4 px-3 py-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
                    >
                      <div 
                        className="w-10 h-10 rounded-lg flex-shrink-0 border border-gray-200"
                        style={{ backgroundColor: product.colorHex }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{product.shadeName}</p>
                        <p className="text-sm text-gray-500">{product.productCode} • {product.finishType}</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No results message */}
            {showSuggestions && searchQuery.length > 0 && searchResults.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 p-6 z-50 text-center">
                <p className="text-gray-500">No shades found for &ldquo;{searchQuery}&rdquo;</p>
              </div>
            )}
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {finishTypes.map((type) => (
              <button
                key={type}
                onClick={() => setActiveFilter(type)}
                data-testid={`filter-${type.toLowerCase()}`}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeFilter === type
                    ? 'bg-blue-600 text-white shadow-lg scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                {type}
              </button>
            ))}
          </div>

          {/* Results count */}
          {searchQuery && (
            <p className="text-center text-gray-500 mb-6">
              Showing {filteredProducts.length} result{filteredProducts.length !== 1 ? 's' : ''} 
              {searchQuery && ` for "${searchQuery}"`}
            </p>
          )}

          {/* Product Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                onClick={() => handleProductClick(product.id)}
                data-testid={`product-card-${product.id}`}
                className="group cursor-pointer"
              >
                <div className="bg-white rounded-3xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-xl hover:border-gray-300 transition-all duration-300 group-hover:scale-105">
                  {/* Color Swatch */}
                  <div 
                    className="aspect-square relative overflow-hidden"
                    style={{ 
                      background: product.colorHex || '#ccc'
                    }}
                  >
                    {/* Overlay on Hover */}
                    <div className="absolute inset-0 bg-black/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                      <span className="text-white text-sm font-medium px-4 py-2 bg-black/50 rounded-full backdrop-blur-sm">
                        View Details
                      </span>
                    </div>
                  </div>
                  
                  {/* Product Info */}
                  <div className="p-4 bg-white">
                    <h3 className="text-sm font-medium text-gray-900 mb-1 truncate">{product.shadeName}</h3>
                    <p className="text-xs text-gray-500 mb-1">{product.productCode}</p>
                    <span className="text-xs text-gray-400">{product.finishType}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No products found */}
          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No shades found matching your criteria</p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setActiveFilter('All');
                }}
                className="mt-4 text-blue-600 font-medium hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ShadesGallery;
